<template>
    <footer class="footer">
        <!-- A button, not a link: the privacy notice opens as a dialog so the
             user does not lose their place in the flow. -->
        <BaseButton theme="tertiary" size="sm" class="privacy" @click="emit('openPrivacy')">
            {{ t("onboarding.calendarFirst.footer.privacy") }}
            <i aria-hidden="true" class="bi bi-lock"></i>
        </BaseButton>

        <div class="wordmark-slot">
            <span class="wordmark">
                <svg width="15" height="15" viewBox="0 0 24 24" aria-hidden="true" class="wordmark-glyph">
                    <circle v-for="dot in DOTS" :key="dot.key" :cx="dot.cx" :cy="dot.cy" r="1.7" :fill="dot.fill" :opacity="dot.opacity" />
                    <circle cx="12" cy="12" r="2.2" fill="var(--color-valence-primary-500)" />
                </svg>
                {{ t("onboarding.calendarFirst.footer.wordmark") }}
            </span>
        </div>

        <BaseButton v-if="skippable" theme="tertiary" size="sm" class="skip" :disabled="busy" @click="emit('skip')">
            {{ t("onboarding.calendarFirst.footer.skip") }}
            <i aria-hidden="true" class="bi bi-arrow-right"></i>
        </BaseButton>
    </footer>
</template>

<script setup lang="ts">
import BaseButton from "~vue/components/BaseButton.vue";
import { useI18n } from "vue-i18n";

const { skippable = false, busy = false } = defineProps<{
    skippable?: boolean;
    busy?: boolean;
}>();

const emit = defineEmits<{ openPrivacy: []; skip: [] }>();
const { t } = useI18n();

const DOT_COUNT = 12;
const DOT_RADIUS = 7.5;

const DOTS = Array.from({ length: DOT_COUNT }, (_, index) => {
    const angle = (index / DOT_COUNT) * Math.PI * 2;
    return {
        key: index,
        cx: 12 + Math.cos(angle) * DOT_RADIUS,
        cy: 12 + Math.sin(angle) * DOT_RADIUS,
        fill: index % 3 === 0 ? "var(--color-valence-pink-500)" : "var(--color-valence-primary-500)",
        opacity: 0.55 + (index % 4) * 0.15,
    };
});
</script>

<style scoped>
@reference "../../../css/tailwind.css";

@layer components {
    .footer {
        @apply relative flex flex-wrap items-center justify-between gap-y-3 border-t border-border px-6 pt-4 pb-6 sm:px-10;
    }

    .privacy,
    .skip {
        @apply inline-flex items-center gap-2;
    }

    .wordmark-slot {
        @apply order-last flex w-full justify-center sm:absolute sm:left-1/2 sm:order-none sm:w-auto sm:-translate-x-1/2;
    }

    .wordmark {
        @apply body-small-semibold inline-flex items-center gap-2 text-button-tertiary-text;
    }

    .wordmark-glyph {
        @apply shrink-0;
    }
}
</style>
